"use client";

// @ts-ignore
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useVisitHistorySection } from "@/hooks/modal/useVisitHistorySection";
import type { Visit } from "@/components/custom/appointment-modal/visit-history-section";
import { VisitHistoryPanelMobile, type VisitHistoryPanelStatus } from "./visit-history-panel";

// Format "YYYY-MM-DD" -> "DD.MM"
function toDDMM(iso: string) {
  const [, m, d] = (iso || "").split("-");
  return d && m ? `${d}.${m}` : iso;
}

function mapStatus(status: Visit["status"]): VisitHistoryPanelStatus {
  if (status === "visited") return "arrived";
  if (status === "no_show") return "not-arrived";
  if (status === "cancelled") return "cancelled";
  return "waiting";
}

export function VisitHistorySectionMobile() {
  const { appointment } = useAppointmentModal();
  const { t } = useTranslation();
  const companyId = appointment?.company_id;
  const clientId = appointment?.client?.id;

  const { data: visits = [], isLoading, error } = useVisitHistorySection(companyId, clientId);

  if (!companyId || !clientId) {
    // eslint-disable-next-line no-console
    console.warn("VisitHistorySectionMobile: missing company_id or client.id", { companyId, clientId, appointment });
    return (
      <div className="flex min-h-[120px] items-center justify-center text-orange-600">
        {t("appointment.history.unavailable", "Visit history unavailable")}
      </div>
    );
  }

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{t("appointment.history.loading", "Loading history...")}</div>;
  }
  if (error) {
    return <div className="py-8 text-center text-sm text-red-500">{t("appointment.history.error", "Error loading history")}</div>;
  }

  // Group by date
  const byDate = visits.reduce<Record<string, Visit[]>>((acc, v) => {
    (acc[v.date] ||= []).push(v);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort((a, b) => (a < b ? 1 : -1)); // newest first

  return (
    <div className="space-y-4">
      {dates.map((date) => (
        <div key={date} className="space-y-3">
          <div className="px-1 text-[12px] font-medium uppercase tracking-wide text-gray-500">{toDDMM(date)}</div>
          <div className="space-y-3">
            {byDate[date].map((v) => (
              <VisitHistoryPanelMobile
                key={v.id}
                visitId={v.id}
                date={toDDMM(v.date)}
                time={v.time}
                employeeName={v.employee?.name ?? ""}
                employeeAvatar={v.employee?.photo}
                services={v.services || []}
                status={mapStatus(v.status)}
                location={v.location}
                comment={v.comment}
                attachments={v.attachments}
              />
            ))}
          </div>
        </div>
      ))}

      {dates.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">{t("appointment.history.no_visits", "No visits")}</div>
      )}
    </div>
  );
}
